import { Fonts } from '../@jumbo/constants/ThemeOptions';
import createBreakpoints from '@mui/system/createTheme/createBreakpoints';

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
});

const defaultTheme = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  spacing: 4,
  direction: 'ltr',
  palette: {
    mode: 'dark',
    common: {
      black: '#000',
      white: '#fff',
      dark: 'rgb(36, 36, 36)',
    },
    primary: {
      main: '#000',
      light: '#000',
      dark: 'rgb(36, 36, 36)',
      contrastText: '#ffffff',
      containedContrastText: '#000',
    },
    secondary: {
      main: '#FFD200',
      dark: '#576870',
      light: '#BDDFEF',
      contrastText: '#ffffff',
      containedContrastText: '#000',
    },
    info: {
      light: '#326bb4',
      main: '#00bfdf',
      dark: '#0097a7',
    },
    white: {
      main: '#fff',
    },
    black: {
      main: '#000',
    },
    error: {
      main: '#ed1c24',
      light: '#f27059',
      dark: '#ee4782',
      contrastText: '#fff',
    },
    danger: {
      main: '#ed1c24',
      light: '#f27059',
      dark: '#ee4782',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f4ce35',
      light: '#fbaf3f',
      dark: '#f68a1e',
      contrastText: '#000',
    },
    background: {
      paper: '#eee',
      default: '#eee',
    },
    grey: {
      main: '#808080',
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161',
    },
    sidebar: {
      bgColor: '#fff',
      textColor: 'rgba(0, 0, 0, 0.6)',
      textDarkColor: 'rgba(0, 0, 0, 0.87)',
      textActiveColor: '#6200EE',
      navHoverBgColor: 'rgb(229, 229, 229)',
      navActiveBgColor: 'rgb(239, 229, 253)',
      borderColor: 'rgba(33, 33, 33, 0.08)',
    },
    horizontalNav: {
      navigationColor: 'rgba(255, 255, 255, 0.74)',
      navigationActiveColor: 'rgba(255, 255, 255, 1)',
      textColor: 'rgba(0, 0, 0, 0.6)',
      textDarkColor: 'rgba(0, 0, 0, 0.87)',
      textActiveColor: '#6200EE',
      menuHoverBgColor: 'rgb(229, 229, 229)',
      menuActiveBgColor: 'rgb(239, 229, 253)',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: '#FFD200',
      disabled: 'rgba(0, 0, 0, 0.38)',
      hint: 'rgba(0, 0, 0, 0.3)',
      white: '#fff',
    },
    btn: {
      hover: 'rgba(0, 0, 0, 0.08)',
    },
    lightBtn: {
      bgColor: '#f5f5f5',
      textColor: 'rgba(0, 0, 0, 0.38)',
    },
    borderColor: {
      main: 'rgba(0, 0, 0, 0.06)',
      dark: 'rgba(0, 0, 0, 0.12)',
    },
    popupColor: {
      main: '#fff',
    },
  },
  status: {
    danger: 'orange',
  },
  typography: {
    fontFamily: Fonts.PRIMARY,
    fontWeightExtraLight: 200,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightBold: 'bold',
    fontWeightExtraBold: 800,
  },
  overrides: {
    MuiFab: {
      variants: [
        {
          props: { size: 'xs' },
          style: {
            height: 26,
            width: 26,
            minHeight: 26,
            '& svg': {
              height: 14,
              width: 14,
            },
          },
        },
      ],
    },

    MuiTextField: {
      root: {
        '& label': {
          fontSize: '1rem',
          fontFamily: '"Trade Gothic LT Std",sans-serif',
          textTransform: 'none !important',
          top: '0',
          fontWeight: 400,
          left: '0',
          color: 'rgba(0, 0, 0, 0.6)',
        },
      },
    },

    MuiTypography: {
      h1: {
        fontSize: 36,
        fontWeight: 800,
        textTransform: 'uppercase',
        fontStyle: 'italic',
        fontFamily: '"Trade Gothic Next LT Pro","Trade Gothic LT Pro","Trade Gothic LT Std","T Star Pro",sans-serif',
        [breakpoints.up('lg')]: {
          fontSize: 50,
        },
      },
      h2: {
        fontSize: 20,
        fontWeight: 400,
        textTransform: 'uppercase',
        fontStyle: 'normal',
        fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
        [breakpoints.up('lg')]: {
          fontSize: 32,
        },
      },
      h3: {
        fontSize: 17,
        fontWeight: 400,
        textTransform: 'uppercase',
        fontStyle: 'normal',
        fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
        [breakpoints.up('lg')]: {
          fontSize: 18,
        },
      },
      h4: {
        fontSize: 16,
        textTransform: 'uppercase',
        fontWeight: 400,
        fontStyle: 'normal',
        fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
      },
      h5: {
        fontSize: 14,
        textTransform: 'uppercase',
        fontWeight: 400,
        fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
      },
      h6: {
        fontSize: 14,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
      },
      subtitle1: {
        fontSize: 16,
        fontWeight: 400,
        letterSpacing: 0.15,
      },
      subtitle2: {
        fontSize: 14,
        fontWeight: 'bold',
        letterSpacing: 0.1,
      },
      body1: {
        fontSize: 16,
        fontWeight: 400,
        letterSpacing: 0.5,
      },
      body2: {
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: 0.25,
      },
    },
    MuiButton: {
      root: {
        fontWeight: 'bold',
        fontSize: 13,
        fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
        boxShadow: '0',
      },
    },
    MuiToggleButton: {
      root: {
        borderRadius: 4,
      },
    },
    MuiCardLg: {
      root: {
        borderRadius: 10,
      },
    },
    MuiCard: {
      root: {
        borderRadius: 4,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 2px 1px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.14)',
      },
    },
    MuiTab: {
      textColorPrimary: {
        color: 'rgba(0, 0, 0, 0.87)',
      },
    },
    MuiPopover: {
      paper: {
        backgroundColor: '#FFFFFF',
      },
    },
    MuiDialog: {
      paper: {
        backgroundColor: '#FFFFFF',
      },
    },
  },
};
export default defaultTheme;
